import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  Download,
  RefreshCw,
  Calendar,
  DollarSign,
  Users,
  Zap,
  CreditCard,
  Percent,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const formatAmount = (amount, currency = 'KZT') => {
  if (!amount) return '0';
  if (currency === 'KZT') {
    return `${amount.toLocaleString('ru-RU')} ₸`;
  }
  return `${(amount / 100).toFixed(2)} ${currency}`;
};

const ReportsPage = () => {
  const { hasPermission } = useAuth();
  const { t, language } = useLanguage();
  const [reportType, setReportType] = useState('turnover');
  const [period, setPeriod] = useState('month');
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsPeriod, setAnalyticsPeriod] = useState('30');

  const canExport = hasPermission('reports', 'export');

  const fetchAnalytics = async () => {
    try {
      const endDate = new Date().toISOString().split('T')[0];
      const days = parseInt(analyticsPeriod);
      const startDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      
      const response = await api.get('/analytics/dashboard', {
        params: { start_date: startDate, end_date: endDate }
      });
      setAnalytics(response.data);
    } catch (err) {
      console.error('Analytics error:', err);
    }
  };

  const fetchReport = async () => {
    try {
      setLoading(true);
      const endpoint = `/reports/${reportType}?period=${period}`;
      const response = await api.get(endpoint);
      setReport(response.data);
    } catch (err) {
      toast.error('Ошибка загрузки отчёта');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
    fetchAnalytics();
  }, [reportType, period]);

  useEffect(() => {
    fetchAnalytics();
  }, [analyticsPeriod]);

  const handleExport = async (format) => {
    try {
      const response = await api.get(`/reports/export/csv?report_type=${reportType}&period=${period}`, {
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${reportType}_report.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Отчёт экспортирован');
    } catch (err) {
      toast.error('Ошибка экспорта');
    }
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Analytics Section */}
      <Card className="bg-slate-800/50 border-slate-700/50">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-500" />
              Аналитика платежей
            </CardTitle>
            <Select value={analyticsPeriod} onValueChange={setAnalyticsPeriod}>
              <SelectTrigger className="w-32 bg-slate-700/50 border-slate-600 text-white h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">7 дней</SelectItem>
                <SelectItem value="30">30 дней</SelectItem>
                <SelectItem value="90">90 дней</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <p className="text-xs text-slate-400">Всего платежей</p>
              <p className="text-xl font-bold text-white">{analytics?.total_payments?.toLocaleString() || '0'}</p>
            </div>
            <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
              <p className="text-xs text-slate-400">Оборот</p>
              <p className="text-xl font-bold text-white">{formatAmount(analytics?.total_amount || 0)}</p>
            </div>
            <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <p className="text-xs text-slate-400">Конверсия</p>
              <p className="text-xl font-bold text-white">{analytics?.approval_rate?.toFixed(1) || '0'}%</p>
            </div>
            <div className="p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
              <p className="text-xs text-slate-400">Средний чек</p>
              <p className="text-xl font-bold text-white">{formatAmount(analytics?.avg_payment_amount || 0)}</p>
            </div>
          </div>
          
          {analytics?.top_gateways?.length > 0 && (
            <div className="mt-4">
              <p className="text-sm text-slate-400 mb-2">Топ шлюзы</p>
              <div className="grid lg:grid-cols-3 gap-2">
                {analytics.top_gateways.slice(0, 3).map((gw, i) => (
                  <div key={i} className="flex items-center justify-between p-2 bg-slate-700/30 rounded">
                    <span className="text-white text-sm">{gw.gateway_name}</span>
                    <span className="text-emerald-400 text-sm">{gw.total_payments} платежей</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Отчёты</h1>
          <p className="text-slate-400 mt-1">Детализированные отчёты</p>
        </div>
        <div className="flex items-center gap-2">
          {canExport && (
            <Button
              onClick={() => handleExport('csv')}
              variant="outline"
              className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/20"
            >
              <Download className="w-4 h-4 mr-2" />
              Экспорт CSV
            </Button>
          )}
          <Button
            onClick={fetchReport}
            variant="outline"
            className="border-slate-600 text-slate-300 hover:bg-slate-700"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="w-48">
          <Select value={reportType} onValueChange={setReportType}>
            <SelectTrigger className="bg-slate-700/50 border-slate-600 text-white">
              <SelectValue placeholder="Тип отчёта" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="turnover">Оборот</SelectItem>
              <SelectItem value="merchants">По мерчантам</SelectItem>
              <SelectItem value="gateways">По шлюзам</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-48">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="bg-slate-700/50 border-slate-600 text-white">
              <SelectValue placeholder="Период" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Сегодня</SelectItem>
              <SelectItem value="yesterday">Вчера</SelectItem>
              <SelectItem value="week">Неделя</SelectItem>
              <SelectItem value="month">Месяц</SelectItem>
              <SelectItem value="quarter">Квартал</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <RefreshCw className="w-8 h-8 text-emerald-500 animate-spin" />
        </div>
      ) : report ? (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {reportType === 'turnover' && (
              <>
                <Card className="bg-slate-800/50 border-slate-700/50">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-400">Всего транзакций</p>
                        <p className="text-2xl font-bold text-white">
                          {report.summary?.total_count?.toLocaleString('ru-RU') || 0}
                        </p>
                      </div>
                      <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                        <BarChart3 className="w-6 h-6 text-blue-400" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-slate-800/50 border-slate-700/50">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-400">Успешных</p>
                        <p className="text-2xl font-bold text-emerald-400">
                          {report.summary?.approved_count?.toLocaleString('ru-RU') || 0}
                        </p>
                      </div>
                      <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                        <TrendingUp className="w-6 h-6 text-emerald-400" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-slate-800/50 border-slate-700/50">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-400">Конверсия</p>
                        <p className="text-2xl font-bold text-white">
                          {report.summary?.success_rate || 0}%
                        </p>
                      </div>
                      <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
                        <Zap className="w-6 h-6 text-purple-400" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-slate-800/50 border-slate-700/50">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-400">{t('payments.amount')}</p>
                        <p className="text-2xl font-bold text-white">
                          {formatAmount(report.summary?.approved_amount)}
                        </p>
                      </div>
                      <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center">
                        <DollarSign className="w-6 h-6 text-amber-400" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </div>

          {/* Data Table */}
          <Card className="bg-slate-800/50 border-slate-700/50">
            <CardHeader>
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-emerald-500" />
                Детализация ({report.date_from} — {report.date_to})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-700">
                      {reportType === 'turnover' && (
                        <>
                          <th className="text-left p-4 text-sm font-medium text-slate-400">{t('common.date')}</th>
                          <th className="text-left p-4 text-sm font-medium text-slate-400">{t('common.total')}</th>
                          <th className="text-left p-4 text-sm font-medium text-slate-400">{t('common.success')}</th>
                          <th className="text-left p-4 text-sm font-medium text-slate-400">Отклонено</th>
                          <th className="text-left p-4 text-sm font-medium text-slate-400">Конверсия</th>
                          <th className="text-right p-4 text-sm font-medium text-slate-400">{t('payments.amount')}</th>
                        </>
                      )}
                      {reportType === 'merchants' && (
                        <>
                          <th className="text-left p-4 text-sm font-medium text-slate-400">{t('terminals.merchant')}</th>
                          <th className="text-left p-4 text-sm font-medium text-slate-400">Транзакций</th>
                          <th className="text-left p-4 text-sm font-medium text-slate-400">{t('common.success')}</th>
                          <th className="text-left p-4 text-sm font-medium text-slate-400">Конверсия</th>
                          <th className="text-right p-4 text-sm font-medium text-slate-400">{t('payments.amount')}</th>
                        </>
                      )}
                      {reportType === 'gateways' && (
                        <>
                          <th className="text-left p-4 text-sm font-medium text-slate-400">{t('payments.gateway')}</th>
                          <th className="text-left p-4 text-sm font-medium text-slate-400">Транзакций</th>
                          <th className="text-left p-4 text-sm font-medium text-slate-400">{t('common.success')}</th>
                          <th className="text-left p-4 text-sm font-medium text-slate-400">Конверсия</th>
                          <th className="text-right p-4 text-sm font-medium text-slate-400">{t('payments.amount')}</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {report.data?.slice(0, 30).map((item, index) => (
                      <tr key={index} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                        {reportType === 'turnover' && (
                          <>
                            <td className="p-4 text-white">{item.date}</td>
                            <td className="p-4 text-slate-300">{item.total_count?.toLocaleString('ru-RU')}</td>
                            <td className="p-4 text-emerald-400">{item.approved_count?.toLocaleString('ru-RU')}</td>
                            <td className="p-4 text-red-400">{item.declined_count?.toLocaleString('ru-RU')}</td>
                            <td className="p-4">
                              <Badge 
                                variant="outline" 
                                className={item.success_rate >= 50 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}
                              >
                                {item.success_rate}%
                              </Badge>
                            </td>
                            <td className="p-4 text-right text-white font-medium">
                              {formatAmount(item.approved_amount)}
                            </td>
                          </>
                        )}
                        {reportType === 'merchants' && (
                          <>
                            <td className="p-4 text-white font-mono text-sm">{item.merchant_name}</td>
                            <td className="p-4 text-slate-300">{item.total_count?.toLocaleString('ru-RU')}</td>
                            <td className="p-4 text-emerald-400">{item.approved_count?.toLocaleString('ru-RU')}</td>
                            <td className="p-4">
                              <Badge 
                                variant="outline" 
                                className={item.success_rate >= 50 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}
                              >
                                {item.success_rate}%
                              </Badge>
                            </td>
                            <td className="p-4 text-right text-white font-medium">
                              {formatAmount(item.approved_amount)}
                            </td>
                          </>
                        )}
                        {reportType === 'gateways' && (
                          <>
                            <td className="p-4 text-white">{item.gateway_alias}</td>
                            <td className="p-4 text-slate-300">{item.total_count?.toLocaleString('ru-RU')}</td>
                            <td className="p-4 text-emerald-400">{item.approved_count?.toLocaleString('ru-RU')}</td>
                            <td className="p-4">
                              <Badge 
                                variant="outline" 
                                className={item.success_rate >= 50 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}
                              >
                                {item.success_rate}%
                              </Badge>
                            </td>
                            <td className="p-4 text-right text-white font-medium">
                              {formatAmount(item.approved_amount)}
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      ) : (
        <div className="text-center text-slate-400 py-20">
          Нет данных для отображения
        </div>
      )}
    </div>
  );
};

export default ReportsPage;
